import { MutableRefObject, useCallback, useRef } from "react";
import { sendGtmEvent } from "~/analytics/definitions";

const getCurrentPercent = (currentTime = 0, duration = 1): number => {
  return Math.round((currentTime / duration) * 100);
};

const useGtmVideoTracking = (
  videoRef: MutableRefObject<HTMLVideoElement | null>,
  videoUrl: string,
  title: string,
  extendedEventLabel?: string,
) => {
  const percentBreakpointsRef = useRef<-1 | 0 | 25 | 50 | 75 | 100>(-1);

  const sendGtmVideoEvent = useCallback(
    (videoPercent: number, videoStatus: string, eventAction: string) => {
      sendGtmEvent({
        event: "video_tracking",
        eventAction,
        eventLabel: extendedEventLabel
          ? `${videoUrl} - ${extendedEventLabel}`
          : videoUrl,
        htmlVideoPercent: videoPercent,
        htmlVideoStatus: videoStatus,
        htmlVideoTitle: title,
        htmlVideoUrl: videoUrl,
        pageUrl: window.location.href,
        htmlVideoCurrentTime: videoRef.current?.currentTime
          ? Math.round(videoRef.current?.currentTime)
          : 0,
        htmlVideoDuration: videoRef.current?.duration
          ? Math.round(videoRef.current?.duration)
          : undefined,
      });
    },
    [extendedEventLabel, videoUrl, title, videoRef],
  );

  const sendGtmPlayEvent = useCallback(() => {
    const currentPercent = getCurrentPercent(
      videoRef.current?.currentTime,
      videoRef.current?.duration,
    );
    if (videoRef.current) {
      if (videoRef.current.currentTime === 0) {
        sendGtmVideoEvent(0, "Initial Play", "Initial Play");
      } else {
        sendGtmVideoEvent(currentPercent, "Resume Play", "Resume Play");
      }
    }
  }, [sendGtmVideoEvent, videoRef]);

  const sendGtmProgressEvent = useCallback(() => {
    const currentPercent = getCurrentPercent(
      videoRef.current?.currentTime,
      videoRef.current?.duration,
    );

    if (videoRef?.current) {
      if (currentPercent === 100 && percentBreakpointsRef.current !== 100) {
        percentBreakpointsRef.current = 100;
        sendGtmVideoEvent(100, "complete", "Reached the end");
      } else if (currentPercent >= 75 && percentBreakpointsRef.current < 75) {
        percentBreakpointsRef.current = 75;
        sendGtmVideoEvent(75, "progress", "Reached 75%");
      } else if (currentPercent >= 50 && percentBreakpointsRef.current < 50) {
        percentBreakpointsRef.current = 50;
        sendGtmVideoEvent(50, "progress", "Reached 50%");
      } else if (currentPercent >= 25 && percentBreakpointsRef.current < 25) {
        percentBreakpointsRef.current = 25;
        sendGtmVideoEvent(25, "progress", "Reached 25%");
      }
    }
  }, [sendGtmVideoEvent, videoRef]);

  const sendGtmPauseEvent = useCallback(() => {
    const currentPercent = getCurrentPercent(
      videoRef.current?.currentTime,
      videoRef.current?.duration,
    );
    // onPause technically fires at the video end, this prevents an unnecessary event from firing
    if (currentPercent < 100) {
      sendGtmVideoEvent(currentPercent, "pause", "Paused");
    }
  }, [sendGtmVideoEvent, videoRef]);

  return { sendGtmProgressEvent, sendGtmPauseEvent, sendGtmPlayEvent };
};

export default useGtmVideoTracking;
